import { useState } from "react";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import type { Client } from "@/models/Dashboards";
import FormModal from "../common/HPFormModal";
import {
  Box,
  Alert,
  FormLabel,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import HPDropdown from "../common/Dropdown";
import HPInput from "../common/Input";
import HPButton from "../common/Button";
import { useChartModal } from "@/hooks/use-chart-modal";
import { QuestionsList } from "./chart-modal/QuestionsList";
import DatasetSelector from "./chart-modal/DatasetSelector";
import RollupBuilder from "./chart-modal/RollupBuilder";
import BarChartMetricSourceSelector, {
  type ResolvedBarChartMetricSelection,
} from "./chart-modal/BarChartMetricSourceSelector";
import LineChartMetricSourceSelector, {
  type ResolvedLineChartMetricSelection,
} from "./chart-modal/LineChartMetricSourceSelector";
import type { LineFocusMode } from "@/store/slices/line-chart-config-slice";
import type { WidgetRollup } from "@/models/Rollup";
import type { MetricBase, MetricPopulation, MetricRepresentedResult } from "@/models/Metric";
import { CHART_TYPE_LABELS } from "@/utils/chart-utils";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";
import { filterVisiblePreservingSelected } from "@/utils/chart-visibility-utils";
import { t } from "@/utils/i18n";
// import { AutoUpdateToggle } from "./chart-modal/AutoUpdateToggle";

interface CreateChartModalProps {
  action: (
    id: string,
    datasetId: string,
    datasetVersionId?: number | null,
    focusParams?: {
      focusMode: string;
      focusEntity: string;
      focusEntityDisplayLabel?: string;
      questionType?: string;
      focusRollup?: WidgetRollup;
      focusRollupName?: string;
      /** single_one_dimensional metric rollup only — see bar-chart-metric-utils.ts. */
      categoryRollups?: WidgetRollup[];
      datasetMetric?: {
        id: string;
        name: string;
        base: MetricBase;
        population: MetricPopulation;
        representedResults: MetricRepresentedResult[];
      };
    },
    timeUnit?: string,
  ) => void;
  initialValue?: string;
  chartType:
    | "pie"
    | "bar"
    | "grouped_bar"
    | "stacked_bar"
    | "line"
    | "donut"
    | "table"
    | "crosstab";
  isCreatingChart?: boolean;
  hasActiveFilters?: boolean;
  existingDatasetIds?: string[];
  primaryClient?: Client;
  additionalClients?: Client[];
  dashboardId?: string;
}

const CreateChartModal: React.FC<CreateChartModalProps> = ({
  action,
  chartType,
  isCreatingChart = false,
  hasActiveFilters = false,
  existingDatasetIds = [],
  primaryClient,
  additionalClients = [],
  dashboardId,
}) => {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);

  const {
    datasetId,
    datasetVersionId,
    searchTerm,
    selectedQuestionId,
    selectedQuestionType,
    autoUpdate,
    datasetGroups,
    isAutoSelected,
    filteredQuestions,
    recentQuestionIds,
    isFetching,
    hasMore,
    observerTarget,
    isDatasetRestricted,
    datasetSearchTerm,
    datasetsHasMore,
    handleDatasetSearchChange,
    isFetchingDatasets,
    observerTargetDatasets,
    handleDatasetChange,
    handleQuestionChange,
    handleQuestionSelect,
    clearQuestionSelection,
    setAutoUpdate,
    // Line chart focus mode state
    selectedQuestionDetails,
    focusMode,
    selectedVariableId,
    selectedValueId,
    selectedVariableLabel,
    selectedValueValue,
    timeUnit,
    enabledTimeUnits,
    needsFocusSelection,
    handleFocusModeChange,
    handleVariableSelect,
    handleValueSelect,
    handleTimeUnitChange,
    focusMetric,
    handleFocusMetricChange,
    rollupId,
    rollupName,
    rollupSelectedLabels,
    isRollupValid,
    handleRollupNameChange,
    handleRollupLabelsChange,
    isFetchingQuestionDetails,
  } = useChartModal(chartType, { hasActiveFilters, existingDatasetIds, primaryClient, additionalClients, dashboardId });

  // Chart Source (Dataset Metrics in Bar/Line Chart) — Bar and Line Chart
  // only, defaults to Question. Kept as local state rather than folded into
  // useChartModal's reducer since it's specific to these two chart types and
  // doesn't interact with the shared question-search state at all. Shared
  // between the two chart types since a single modal instance is only ever
  // one chart type at a time.
  const [chartSource, setChartSource] = useState<"question" | "metric">("question");
  const [resolvedMetricSelection, setResolvedMetricSelection] = useState<
    ResolvedBarChartMetricSelection | ResolvedLineChartMetricSelection | null
  >(null);

  const handleChartSourceChange = (source: "question" | "metric") => {
    setChartSource(source);
    // Changing Chart Source clears the prior source selection and its
    // dependent settings.
    setResolvedMetricSelection(null);
  };

  const handleDatasetChangeAndClearMetric = (id: string) => {
    handleDatasetChange(id);
    // Changing Dataset clears the selected source.
    setResolvedMetricSelection(null);
  };

  const handleCreate = () => {
    if ((chartType === "bar" || chartType === "line") && chartSource === "metric") {
      if (!resolvedMetricSelection) return;
      const { metric, focusParams, representedResults } = resolvedMetricSelection;
      action(
        metric.source!.question.questionId,
        datasetId,
        datasetVersionId,
        {
          ...focusParams,
          focusMode: focusParams.focusMode ?? "",
          focusEntity: focusParams.focusEntity ?? "",
          datasetMetric: {
            id: metric.id,
            name: metric.name,
            base: metric.base,
            population: metric.population,
            representedResults,
          },
        },
        chartType === "line" ? timeUnit : undefined,
      );
      return;
    }
    // For crosstab, we don't need a question - just dataset
    // DatasetId and datasetVersionId are now saved by useChartCreation hook when chart is created
    if (chartType === "crosstab") {
      action("", datasetId, datasetVersionId);
    } else if (chartType === "line") {
      // For line charts, always pass timeUnit
      if (needsFocusSelection) {
        if (focusMetric === "rollup") {
          action(
            selectedQuestionId,
            datasetId,
            datasetVersionId,
            {
              focusMode: (focusMode || "variable").toUpperCase(),
              focusEntity: "",
              focusRollup: {
                id: rollupId,
                calculationType: "SUM_SELECTED",
                sourceCategoryLabels: rollupSelectedLabels,
              },
              focusRollupName: rollupName,
              questionType: selectedQuestionType ?? undefined,
            },
            timeUnit,
          );
          return;
        }

        // With focus selection, pass focus parameters
        const currentFocusMode = focusMode || "variable";

        // Get the display label from the dropdown
        let focusEntityDisplayLabel = "";
        if (currentFocusMode === "variable") {
          const variable = allVariables.find(
            (v) => v.id === selectedVariableId,
          );
          focusEntityDisplayLabel = variable?.rowtitle || variable?.title || "";
        } else {
          const valueObj = allValues.find((v) => v.id === selectedValueId);
          focusEntityDisplayLabel =
            valueObj?.rowtitle || valueObj?.title || valueObj?.label || "";
        }

        const focusEntity =
          currentFocusMode === "variable"
            ? selectedVariableLabel
            : selectedValueValue;

        action(
          selectedQuestionId,
          datasetId,
          datasetVersionId,
          {
            focusMode: currentFocusMode.toUpperCase(),
            focusEntity: focusEntity || "",
            focusEntityDisplayLabel: focusEntityDisplayLabel,
            questionType: selectedQuestionType ?? undefined,
          },
          timeUnit,
        );
      } else {
        // Without focus selection, pass undefined for focusParams but include timeUnit
        action(
          selectedQuestionId,
          datasetId,
          datasetVersionId,
          { focusMode: "", focusEntity: "", questionType: selectedQuestionType ?? undefined },
          timeUnit,
        );
      }
    } else if (chartType === "grouped_bar") {
      // Grouped bar only needs the Group By level. No specific entity and no
      // rollup — focusEntity/focusRollup stay null. The backend focusMode is
      // inverted relative to the Group By label (VALUE puts variables on the
      // x-axis), matching the edit drawer so the selection stays consistent.
      const groupByLevel = focusMode || "variable";
      action(selectedQuestionId, datasetId, datasetVersionId, {
        focusMode: groupByLevel === "variable" ? "VALUE" : "VARIABLE",
        focusEntity: "",
      });
    } else if (chartType === "stacked_bar") {
      action(selectedQuestionId, datasetId, datasetVersionId, {
        focusMode: "VALUE",
        focusEntity: "",
      });
    } else if (chartType === "bar") {
      // For bar charts with focus mode
      if (needsFocusSelection) {
        if (focusMetric === "rollup") {
          action(selectedQuestionId, datasetId, datasetVersionId, {
            focusMode: (focusMode || "variable").toUpperCase(),
            focusEntity: "",
            focusRollup: {
              id: rollupId,
              calculationType: "SUM_SELECTED",
              sourceCategoryLabels: rollupSelectedLabels,
            },
            focusRollupName: rollupName,
          });
        } else {
          const currentFocusMode = focusMode || "variable";
          let focusEntityDisplayLabel = "";
          if (currentFocusMode === "variable") {
            const variable = allVariables.find(
              (v) => v.id === selectedVariableId,
            );
            focusEntityDisplayLabel = variable?.rowtitle || variable?.title || "";
          } else {
            const valueObj = allValues.find((v) => v.id === selectedValueId);
            focusEntityDisplayLabel =
              valueObj?.rowtitle || valueObj?.title || valueObj?.label || "";
          }
          const focusEntity =
            currentFocusMode === "variable"
              ? selectedVariableLabel
              : selectedValueValue;

          action(selectedQuestionId, datasetId, datasetVersionId, {
            focusMode: currentFocusMode.toUpperCase(),
            focusEntity: focusEntity || "",
            focusEntityDisplayLabel: focusEntityDisplayLabel,
          });
        }
      } else {
        // Without focus selection
        action(selectedQuestionId, datasetId, datasetVersionId);
      }
    } else if (chartType === "donut") {
      // Donut only supports Variable focus for Single Grid questions.
      if (needsFocusSelection) {
        if (focusMetric === "rollup") {
          action(selectedQuestionId, datasetId, datasetVersionId, {
            focusMode: "VARIABLE",
            focusEntity: "",
            focusRollup: {
              id: rollupId,
              calculationType: "SUM_SELECTED",
              sourceCategoryLabels: rollupSelectedLabels,
            },
            focusRollupName: rollupName,
          });
        } else {
          const variable = allVariables.find((v) => v.id === selectedVariableId);
          const focusEntityDisplayLabel = variable?.rowtitle || variable?.title || "";

          action(selectedQuestionId, datasetId, datasetVersionId, {
            focusMode: "VARIABLE",
            focusEntity: selectedVariableLabel || "",
            focusEntityDisplayLabel,
          });
        }
      } else {
        action(selectedQuestionId, datasetId, datasetVersionId);
      }
    } else {
      // For all other chart types
      action(selectedQuestionId, datasetId, datasetVersionId);
    }
  };

  // Once a question is selected, collapse the list to just that question so the
  // Focus Selection section below is reachable without scrolling past the whole
  // list. "Change question" clears the selection to expand the list again.
  const selectedQuestionRow = selectedQuestionId
    ? filteredQuestions.find((q) => q.datasetElementId === selectedQuestionId)
    : undefined;
  const isQuestionCollapsed = !!selectedQuestionRow;
  const displayedQuestions = isQuestionCollapsed
    ? [selectedQuestionRow!]
    : filteredQuestions;

  // Get all variables / values from question details.
  const allVariables = selectedQuestionDetails
    ? [
        ...(selectedQuestionDetails.rowVariables || []),
        ...(selectedQuestionDetails.columnVariables || []),
      ]
    : [];
  const allValues = selectedQuestionDetails?.values || [];

  // A grid Bar Chart needs at least one visible variable AND one visible value:
  // Variable Focus renders a variable's values as bars, Value Focus renders a
  // value across variables — so a fully-hidden dimension leaves nothing to
  // chart. In that case the chart can't be created; block Continue and explain
  // in place of the focus picker.
  const visibleVariableCount = allVariables.filter((v) => !v.hidden).length;
  const visibleValueCount = allValues.filter((v) => !v.hidden).length;
  const isGridFocusBlocked =
    chartType === "bar" &&
    needsFocusSelection &&
    (visibleVariableCount === 0 || visibleValueCount === 0);

  // A Table's rows come from the question's variables — if every variable is
  // hidden in the dataset (but the question itself is still selectable), there's
  // nothing left to show as a row. Block Continue and explain, same as the Bar
  // grid focus case above.
  const isTableRowsBlocked =
    chartType === "table" &&
    !!selectedQuestionDetails &&
    allVariables.length > 0 &&
    visibleVariableCount === 0;

  // Check if create button should be disabled
  const isCreateDisabled = () => {
    if (isGridFocusBlocked) return true;
    if (isTableRowsBlocked) return true;

    if (chartType === "line" && chartSource === "metric") {
      // PRD-67: Continue enables once the source selection AND Time Unit resolve.
      return !datasetId || !resolvedMetricSelection || !timeUnit;
    }

    if (chartType === "bar" && chartSource === "metric") {
      return !datasetId || !resolvedMetricSelection;
    }

    if (chartType === "crosstab") {
      return !datasetId;
    }

    // Grouped bar only needs the focus level, which always has a default.
    if (chartType === "grouped_bar") {
      return !selectedQuestionId || !datasetId;
    }

    // Stacked bar has no focus-level choice at all — just dataset + question.
    if (chartType === "stacked_bar") {
      return !selectedQuestionId || !datasetId;
    }

    // Donut only supports Variable focus for Single Grid questions — no
    // value focus, but a Focus Rollup of variables is supported.
    if (chartType === "donut" && needsFocusSelection) {
      if (focusMetric === "rollup") {
        if (!isRollupValid) return true;
      } else if (!selectedVariableId) {
        return true;
      }
    }

    // For line and bar charts with focus mode selection needed
    if ((chartType === "line" || chartType === "bar") && needsFocusSelection) {
      if (focusMetric === "rollup") {
        if (!isRollupValid) return true;
      } else {
        const currentFocusMode = focusMode || "variable";
        if (currentFocusMode === "variable" && !selectedVariableId) return true;
        if (currentFocusMode === "value" && !selectedValueId) return true;
      }
    }

    return !selectedQuestionId || !datasetId;
  };

  return (
    <FormModal
      title={`New ${CHART_TYPE_LABELS[chartType]} Chart`}
      confirmButtonText="Continue"
      isConfirmButtonDisabled={isCreateDisabled() || isFetchingQuestionDetails}
      loading={isCreatingChart || isFetchingQuestionDetails}
      onConfirm={handleCreate}
      onCancel={closeTopModal}
    >
      <Box className="flex flex-col gap-2">
        {/* <AutoUpdateToggle checked={autoUpdate} onChange={setAutoUpdate} /> */}

        {isDatasetRestricted && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Dataset selection is restricted to existing datasets because filters
            are currently applied to the dashboard. To add a chart from a
            different dataset, please reset the filters first.
          </Alert>
        )}

        <DatasetSelector
          datasetGroups={datasetGroups}
          selectedDatasetId={datasetId || ""}
          isAutoSelected={isAutoSelected}
          searchTerm={datasetSearchTerm}
          onSearchChange={handleDatasetSearchChange}
          isFetching={isFetchingDatasets}
          hasMore={datasetsHasMore}
          observerTarget={observerTargetDatasets}
          onSelect={(id) => handleDatasetChangeAndClearMetric(id)}
        />

        {/* Time Unit Selector for Line Charts — kept above Chart Source per
            PRD-67 for both the Question and Metric paths. */}
        {chartType === "line" && (
          <HPDropdown
            label="Time Unit*"
            value={timeUnit}
            onChange={(value) =>
              handleTimeUnitChange(
                value as "QUARTER" | "SEMESTER" | "MONTH" | "WEEK",
              )
            }
            placeholder="Select time unit"
            options={(
              [
                { value: "QUARTER", label: "Quarter" },
                { value: "SEMESTER", label: "Semester" },
                { value: "MONTH", label: "Month" },
                { value: "WEEK", label: "Week" },
              ] as const
            ).filter(
              (o) =>
                !enabledTimeUnits ||
                enabledTimeUnits.includes(
                  o.value as "QUARTER" | "SEMESTER" | "MONTH" | "WEEK",
                ),
            )}
          />
        )}

        {datasetId && (chartType === "bar" || chartType === "line") && (
          <Box sx={{ mt: 1 }}>
            <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5, display: "block" }}>
              Chart Source
            </FormLabel>
            <RadioGroup
              row
              value={chartSource}
              onChange={(e) =>
                handleChartSourceChange(e.target.value as "question" | "metric")
              }
            >
              <FormControlLabel value="question" control={<Radio size="small" />} label="Question" />
              <FormControlLabel value="metric" control={<Radio size="small" />} label="Metric" />
            </RadioGroup>
          </Box>
        )}

        {datasetId && chartType === "bar" && chartSource === "metric" && (
          <BarChartMetricSourceSelector
            datasetId={datasetId}
            onResolvedChange={setResolvedMetricSelection}
          />
        )}

        {datasetId && chartType === "line" && chartSource === "metric" && (
          <LineChartMetricSourceSelector
            datasetId={datasetId}
            onResolvedChange={setResolvedMetricSelection}
          />
        )}

        {datasetId &&
          chartType !== "crosstab" &&
          !((chartType === "bar" || chartType === "line") && chartSource === "metric") && (
          <>
            {isQuestionCollapsed ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormLabel sx={{ fontSize: 12, color: "#262626" }}>
                  Selected Question
                </FormLabel>
                <HPButton
                  size="small"
                  variant="text"
                  onClick={clearQuestionSelection}
                >
                  Change question
                </HPButton>
              </Box>
            ) : (
              <HPInput
                label="Select Question*"
                value={searchTerm || ""}
                onChange={handleQuestionChange}
                placeholder="Search by label or title..."
              />
            )}

            {chartType === "grouped_bar" &&
            !isFetching &&
            filteredQuestions.length === 0 ? (
              <Alert severity="info">
                Grouped Bar Charts require a Single question with multiple
                variables and multiple values.
              </Alert>
            ) : chartType === "stacked_bar" &&
              !isFetching &&
              filteredQuestions.length === 0 ? (
              <Alert severity="info">
                Stacked Bar Charts require a Single question with multiple
                variables and values.
              </Alert>
            ) : (
              <QuestionsList
                questions={displayedQuestions}
                selectedQuestionId={selectedQuestionId}
                onQuestionSelect={handleQuestionSelect}
                searchTerm={searchTerm}
                isFetching={isQuestionCollapsed ? false : isFetching}
                hasMore={isQuestionCollapsed ? false : hasMore}
                observerTarget={observerTarget}
                recentQuestionIds={recentQuestionIds}
                hideLoadMore={isQuestionCollapsed}
              />
            )}

            {isTableRowsBlocked && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                All variables for this question are hidden in the dataset, so
                a Table can&apos;t be created from it. Restore content in
                Questions to continue.
              </Alert>
            )}

            {/* Line/Bar Chart Focus Mode Selection */}
            {needsFocusSelection && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {chartType === "grouped_bar"
                    ? "Group By*"
                    : "Focus Selection*"}
                </Typography>
                {isGridFocusBlocked ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    All variables or values for this question are hidden in the
                    dataset, so a Bar Chart can&apos;t be created from it.
                    Restore content in Questions to continue.
                  </Alert>
                ) : (
                  <>
                {chartType !== "grouped_bar" && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    {chartType === "donut"
                      ? "This question has multiple variables. Select which variable's values to show as slices:"
                      : "This question has multiple variables. Choose how to display the data:"}
                  </Typography>
                )}

                {/* Donut only supports Variable focus for grid questions — no
                    value-focus choice to make. */}
                {chartType !== "donut" && (
                  <RadioGroup
                    value={focusMode || "variable"}
                    onChange={(e) =>
                      handleFocusModeChange(e.target.value as LineFocusMode)
                    }
                    sx={{ mb: 1 }}
                  >
                    <FormControlLabel
                      value="variable"
                      control={<Radio size="small" />}
                      label={
                        chartType === "grouped_bar"
                          ? "Variables (variables are groups, values are series)"
                          : chartType === "line"
                            ? "Focus on a specific variable (show all its values as lines)"
                            : "Focus on a specific variable (show all its values as bars)"
                      }
                    />
                    <FormControlLabel
                      value="value"
                      control={<Radio size="small" />}
                      label={
                        chartType === "grouped_bar"
                          ? "Values (values are groups, variables are series)"
                          : chartType === "line"
                            ? "Focus on a specific value (show it across all variables as lines)"
                            : "Focus on a specific value (show it across all variables as bars)"
                      }
                    />
                  </RadioGroup>
                )}

                {chartType !== "grouped_bar" && (() => {
                  const activeCategories =
                    focusMode === "value" ? allValues : allVariables;
                  const showMetricToggle =
                    (chartType === "bar" ||
                      chartType === "line" ||
                      chartType === "donut") &&
                    activeCategories.length >= 2;
                  return (
                    <>
                      {showMetricToggle && (
                        <Box sx={{ mt: 1 }}>
                          <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5, display: "block" }}>
                            Response Selection
                          </FormLabel>
                          <RadioGroup
                            value={focusMetric}
                            onChange={(e) => handleFocusMetricChange(e.target.value as "single" | "rollup")}
                          >
                            <FormControlLabel
                              value="single"
                              control={<Radio size="small" />}
                              label="Single Category"
                            />
                            <FormControlLabel
                              value="rollup"
                              control={<Radio size="small" />}
                              label="Rollup"
                            />
                          </RadioGroup>
                        </Box>
                      )}

                      {focusMetric === "rollup" ? (
                        <Box sx={{ mt: 2 }}>
                          <RollupBuilder
                            focusMode={focusMode || "variable"}
                            allVariables={allVariables}
                            allValues={allValues}
                            rollupName={rollupName}
                            rollupSelectedLabels={rollupSelectedLabels}
                            onRollupNameChange={handleRollupNameChange}
                            onSelectedLabelsChange={handleRollupLabelsChange}
                          />
                        </Box>
                      ) : (
                        <>
                          {(focusMode === "variable" || !focusMode) && (() => {
                            const variableOptions = filterVisiblePreservingSelected(
                              allVariables,
                              (v) => v.id === selectedVariableId,
                            );
                            const isSelectedVariableHidden = allVariables.some(
                              (v) => v.id === selectedVariableId && v.hidden,
                            );
                            return (
                              <Box sx={{ mt: 2 }}>
                                <HPDropdown
                                  label="Select Variable*"
                                  value={selectedVariableId || ""}
                                  onChange={(value) => {
                                    const variable = allVariables.find(
                                      (v) => v.id === value,
                                    );
                                    handleVariableSelect(value, variable?.label);
                                  }}
                                  placeholder="Choose a variable"
                                  options={variableOptions.map((v) => ({
                                    value: v.id,
                                    label: v.rowtitle || v.title,
                                    subtitle: v.hidden
                                      ? t("common.hiddenInDataset")
                                      : undefined,
                                  }))}
                                  getOptionDisabled={(option) =>
                                    variableOptions.find((v) => v.id === option.value)
                                      ?.hidden === true &&
                                    option.value !== selectedVariableId
                                  }
                                />
                                {isSelectedVariableHidden && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <HiddenInDatasetBadge />
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}

                          {focusMode === "value" && (() => {
                            const valueOptions = filterVisiblePreservingSelected(
                              allValues,
                              (v) => v.id === selectedValueId,
                            );
                            const isSelectedValueHidden = allValues.some(
                              (v) => v.id === selectedValueId && v.hidden,
                            );
                            return (
                              <Box sx={{ mt: 2 }}>
                                <HPDropdown
                                  label="Select Value*"
                                  value={selectedValueId || ""}
                                  onChange={(value) => {
                                    const valueObj = allValues.find(
                                      (v) => v.id === value,
                                    );
                                    handleValueSelect(
                                      value,
                                      valueObj
                                        ? String(valueObj.value)
                                        : undefined,
                                    );
                                  }}
                                  placeholder="Choose a value"
                                  options={valueOptions.map((v) => ({
                                    value: v.id,
                                    label: v.title || v.label,
                                    subtitle: v.hidden
                                      ? t("common.hiddenInDataset")
                                      : undefined,
                                  }))}
                                  getOptionDisabled={(option) =>
                                    valueOptions.find((v) => v.id === option.value)
                                      ?.hidden === true &&
                                    option.value !== selectedValueId
                                  }
                                />
                                {isSelectedValueHidden && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <HiddenInDatasetBadge />
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </>
                      )}
                    </>
                  );
                })()}
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </FormModal>
  );
};

export default CreateChartModal;
